import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { flatten, isEmpty, uniqBy } from 'lodash';
import useClaimFilters from 'module/claims/hooks/useClaimFilters';
import useClaimStatuses from 'module/claims/hooks/useClaimStatuses';
import { useClaimJobProvider } from '../ClaimJob.Hooks';
import { ClaimSwitchListButton } from '../other/Claim.SwitchListButton';

export default function ClaimFilters() {
  const { lang } = getLocales();
  const { listApi: claimJobListApi, dealersApi, userApi, vehicleMakeApi } = useClaimJobProvider();
  const { claimStatuses, claimJobStatuses } = useClaimStatuses();
  const { userId, permissions } = useAuthStore();
  const { defaultFilters } = useClaimFilters();

  const WITHOUT_OWNER = -1;
  const withoutOwnerData = {
    id: WITHOUT_OWNER,
    title: 'N / A',
    media: null,
    icon: <Icon class="fas-user" />,
  };

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      search={claimJobListApi?.body?.search}
      onSearch={(search) => {
        claimJobListApi?.execute((body) => ({ ...body, search, offset: 0 }));
      }}
      filters={claimJobListApi?.body}
      onOpen={() => {
        if (!vehicleMakeApi?.payload) vehicleMakeApi.execute((b) => b);
        if (!userApi?.payload) userApi.execute((b) => b);
        if (!dealersApi?.payload) dealersApi.execute((b) => b);
      }}
      onFilter={(newFilters) => {
        if (newFilters.claimOwnerId?.includes(WITHOUT_OWNER)) {
          newFilters = {
            ...newFilters,
            claimOwnerId: newFilters.claimOwnerId.filter((id) => id !== WITHOUT_OWNER),
            includeOwnerlessClaim: true,
          };
        }

        claimJobListApi.execute({ ...newFilters, offset: 0 });
      }}
      onReset={() => {
        claimJobListApi.execute(defaultFilters);
      }}
      headerActions={<ClaimSwitchListButton />}
      render={({ tempFilters, setFilters }) => {
        const dealers = dealersApi?.payload?.data ?? [];

        const products = uniqBy(
          !isEmpty(tempFilters?.dealerId)
            ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
            : flatten(dealers.map((d) => d.products)),
          (p) => p.id
        );

        return (
          <>
            <SelectInput
              name="statusCode"
              label={lang.status}
              data={claimJobStatuses}
              isMultiselect
              value={tempFilters?.statusCode}
              onChange={setFilters}
              icon={<Icon class="fas-check" />}
            />
            <SelectInput
              name="claimStatusCode"
              label={lang.claimStatus}
              data={claimStatuses}
              isMultiselect
              value={tempFilters?.claimStatusCode}
              onChange={setFilters}
              icon={<Icon class="fas-check" />}
            />
            <SelectInput
              name="makeId"
              label={lang.vehicleMakes}
              data={vehicleMakeApi?.payload?.data?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-car" />,
              }))}
              isMultiselect
              value={tempFilters?.makeId}
              onChange={setFilters}
              icon={<Icon class="fas-car" />}
            />
            <SelectInput
              name="productId"
              label={lang.products}
              data={products?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-box" />,
              }))}
              isMultiselect
              value={tempFilters?.productId}
              onChange={setFilters}
              icon={<Icon class="fas-box" />}
            />
            <SelectInput
              name="dealerId"
              label={lang.dealers}
              data={dealersApi?.payload?.data?.map((item) => ({
                ...item.displayInfo,
                icon: <Icon class="fas-user-friends" />,
              }))}
              isMultiselect
              value={tempFilters?.dealerId}
              onChange={setFilters}
              icon={<Icon class="fas-user-friends" />}
            />
            <SelectInput
              name="claimOwnerId"
              label={lang.claimOwner}
              data={[
                withoutOwnerData,
                ...(userApi?.payload?.data?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-user" />,
                })) || []),
              ]}
              isMultiselect
              value={[
                ...(tempFilters?.claimOwnerId || []),
                ...(tempFilters?.includeOwnerlessClaim ? [WITHOUT_OWNER] : []),
              ]}
              onChange={({ claimOwnerId }) =>
                setFilters({
                  claimOwnerId,
                  includeOwnerlessClaim: claimOwnerId.includes(WITHOUT_OWNER),
                })
              }
              icon={<Icon class="fas-user" />}
            />
            <DateInput
              name="createdAtFromDateUtc"
              label={lang.createdAtFromDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtFromDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <DateInput
              name="createdAtToDateUtc"
              label={lang.createdAtToDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={tempFilters?.createdAtToDateUtc}
              onChange={setFilters}
              icon={<Icon class="fas-calendar-alt" />}
            />
            {!permissions.VIEW_ONLY_OWNED_CLAIMS && (
              <CheckInput
                name="showMyClaims"
                label={lang.showMyClaims}
                class={inputStyles.check.switch + ' formInput'}
                style={{ marginTop: Theme.Size.M, alignSelf: 'center' }}
                value={tempFilters?.showMyClaims}
                onChange={() =>
                  setFilters({
                    claimReporterId: !tempFilters?.showMyClaims ? userId : null,
                    showMyClaims: !tempFilters?.showMyClaims,
                  })
                }
                htmlElementProps={{ title: tempFilters.showMyClaims ? lang.disable : lang.enable }}
              />
            )}
          </>
        );
      }}
    />
  );
}
